"use client";

import { canonicalWorkAreaKeys } from "~/lib/matching/work-area-keys";
import { FOCUS_AREA_LABELS } from "~/lib/matching/labels";

import { useEffect, useId, useState } from "react";
import { useForm } from "react-hook-form";
import { useRouter } from "next/navigation";
import { Loader2, Pencil, X } from "lucide-react";
import { api } from "~/trpc/react";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import {
  APPROACH_OPTIONS,
  LANGUAGE_OPTIONS,
} from "~/lib/matching/practice-profile";
import type { TherapistSearchCriteria } from "~/lib/ai/match-therapist/search-criteria";
import type { MatchDiagnostics } from "~/lib/ai/match-therapist/match-diagnostics";

const selectClass =
  "border-input bg-background h-10 w-full rounded-md border px-3 text-sm";
const normalize = (text: string) =>
  text
    .toLocaleLowerCase("sr")
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .replace(/đ/g, "dj");
const relaxationLabels = {
  budget: "Bez ograničenja cene",
  languages: "Bez uslova za jezik",
  gender: "Bez uslova za pol terapeuta",
  experience: "Bez uslova za godine iskustva",
  sessionDuration: "Bez uslova za trajanje seanse",
};

function selectionLabel(
  key: string,
  options: readonly { key: string; label: string }[],
) {
  return (
    options.find((option) => option.key === key.toLowerCase())?.label ?? key
  );
}

export function searchCriteriaSummary(
  criteria: TherapistSearchCriteria,
  approaches: readonly { key: string; label: string }[] = APPROACH_OPTIONS,
): string[] {
  const { requirements: r } = criteria;
  return [
    ...criteria.concernKeys.map((key) => criteria.concernLabels[key] ?? key),
    criteria.therapyType === "COUPLES"
      ? "Partnerska terapija"
      : "Individualna terapija",
    ...(criteria.therapistGender === "FEMALE"
      ? ["Terapeutkinja"]
      : criteria.therapistGender === "MALE"
        ? ["Terapeut"]
        : []),
    ...(r.languages ?? []).map((key) => selectionLabel(key, LANGUAGE_OPTIONS)),
    ...(r.maxSessionPrice !== undefined
      ? [`Do ${r.maxSessionPrice} € po seansi`]
      : []),
    ...(r.clientAge !== undefined ? [`Uzrast: ${r.clientAge} godina`] : []),
    ...(r.modalities ?? []).map((key) => selectionLabel(key, approaches)),
    ...(r.minYearsOfExperience !== undefined
      ? [`Najmanje ${r.minYearsOfExperience} godina iskustva`]
      : []),
    ...(r.minSessionDurationMinutes !== undefined
      ? [`Seansa najmanje ${r.minSessionDurationMinutes} min`]
      : []),
    ...(r.excludeTrainees ? ["Samo završena edukacija"] : []),
  ];
}

function MultiSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string[];
  options: readonly { key: string; label: string }[];
  onChange: (value: string[]) => void;
}) {
  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium">
        {label}
        <select
          className={`${selectClass} mt-2`}
          value=""
          onChange={(event) => {
            if (event.target.value) onChange([...value, event.target.value]);
          }}
        >
          <option value="">Dodaj uslov</option>
          {options
            .filter((option) => !value.includes(option.key))
            .map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
        </select>
      </label>
      <ul className="flex flex-wrap gap-2">
        {value.map((key) => (
          <li
            key={key}
            className="bg-secondary inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm"
          >
            {selectionLabel(key, options)}
            <button
              type="button"
              aria-label={`Ukloni uslov: ${selectionLabel(key, options)}`}
              onClick={() => onChange(value.filter((item) => item !== key))}
              className="hover:bg-muted focus-visible:ring-ring rounded p-1 focus-visible:ring-2"
            >
              <X className="size-4" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function SearchCriteriaPanel({
  criteria,
  diagnostics,
  resultId,
  therapyType = "INDIVIDUAL",
  hasMatches,
}: {
  criteria?: TherapistSearchCriteria;
  diagnostics?: MatchDiagnostics;
  resultId: string;
  therapyType?: "INDIVIDUAL" | "COUPLES";
  hasMatches: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const approaches = api.therapistPractice.approaches.useQuery();
  const catalog = api.therapistPractice.catalog.useQuery();
  const [query, setQuery] = useState("");
  const [navigating, setNavigating] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const formId = useId();
  const router = useRouter();
  useEffect(() => {
    if (editing) document.getElementById(`${formId}-concerns`)?.focus();
  }, [editing, formId]);
  const initial: TherapistSearchCriteria = criteria
    ? {
        ...criteria,
        concernKeys: canonicalWorkAreaKeys(criteria.concernKeys),
        concernLabels: {
          ...criteria.concernLabels,
          ...Object.fromEntries(
            canonicalWorkAreaKeys(criteria.concernKeys).map((key) => [
              key,
              catalog.data?.find((entry) => entry.key === key)?.label ??
                criteria.concernLabels[key] ??
                FOCUS_AREA_LABELS[key] ??
                key,
            ]),
          ),
        },
      }
    : {
        version: "structured-v1",
        concernKeys: [],
        concernLabels: {},
        therapistGender: "unknown",
        therapyType,
        requirements: {},
        unsupportedConcerns: [],
      };
  const form = useForm<TherapistSearchCriteria>({ defaultValues: initial });
  const draft = form.watch();
  const refine = api.ai.refineTherapistMatches.useMutation();
  const busy = refine.isPending || navigating;
  const visibleConcerns = (catalog.data ?? []).filter(
    (item) =>
      !draft.concernKeys.includes(item.key) &&
      [item.label, ...(item.aliases ?? []), ...(item.examples ?? [])].some(
        (label) => normalize(label).includes(normalize(query)),
      ),
  );
  const resetEditor = () => {
    form.reset(initial);
    setQuery("");
    setSubmitError(null);
  };
  const openEditor = () => {
    resetEditor();
    setEditing(true);
  };
  const chooseRelaxation = (criterion: keyof typeof relaxationLabels) => {
    resetEditor();
    const next = structuredClone(initial);
    if (criterion === "gender") next.therapistGender = "unknown";
    if (criterion === "budget") delete next.requirements.maxSessionPrice;
    if (criterion === "languages") delete next.requirements.languages;
    if (criterion === "experience")
      delete next.requirements.minYearsOfExperience;
    if (criterion === "sessionDuration")
      delete next.requirements.minSessionDurationMinutes;
    form.reset(next);
    setEditing(true);
  };
  const submit = form.handleSubmit(async (values) => {
    setSubmitError(null);
    try {
      const result = await refine.mutateAsync({ resultId, criteria: values });
      setNavigating(true);
      router.push(`/upitnik/${encodeURIComponent(result.resultId)}`);
    } catch {
      setNavigating(false);
      setSubmitError(
        "Nismo uspeli da ponovimo pretragu. Proveri uslove i pokušaj ponovo.",
      );
    }
  });
  const setNumber = (
    field:
      | "maxSessionPrice"
      | "clientAge"
      | "minYearsOfExperience"
      | "minSessionDurationMinutes",
    value: string,
  ) => {
    form.setValue(
      `requirements.${field}`,
      value === "" ? undefined : Number(value),
      { shouldDirty: true },
    );
  };
  const unsupported = draft.unsupportedConcerns ?? [];
  const noCriteria =
    draft.concernKeys.length === 0 &&
    draft.therapistGender === "unknown" &&
    !Object.values(draft.requirements).some((value) =>
      Array.isArray(value)
        ? value.length > 0
        : value !== undefined && value !== false,
    );

  return (
    <section
      aria-labelledby={`${formId}-title`}
      className="border-border space-y-4 rounded-xl border p-4 sm:p-6"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id={`${formId}-title`} className="text-lg font-bold">
          Uslovi pretrage
        </h2>
        {!editing && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={openEditor}
            aria-expanded={false}
            aria-controls={`${formId}-form`}
          >
            <Pencil className="mr-2 size-4" />
            {criteria ? "Izmeni uslove" : "Postavi uslove"}
          </Button>
        )}
      </div>
      {!editing && (
        <>
          {criteria ? (
            <ul className="flex flex-wrap gap-2" aria-label="Primenjeni uslovi">
              {searchCriteriaSummary(initial, approaches.data ?? []).map(
                (text, index) => (
                  <li
                    key={`${text}-${index}`}
                    className="bg-secondary rounded-md px-3 py-1.5 text-sm"
                  >
                    {text}
                  </li>
                ),
              )}
            </ul>
          ) : (
            <p className="text-muted-foreground text-sm">
              Uslovi ove ranije pretrage nisu sačuvani. Možeš da postaviš nove,
              bez ponovnog popunjavanja upitnika.
            </p>
          )}
          {criteria?.unsupportedConcerns.length ? (
            <p className="text-sm">
              Za ove teme nismo uspeli da odredimo oblast rada:{" "}
              {criteria.unsupportedConcerns.join("; ")}. U izmeni uslova možeš
              da izabereš odgovarajuću oblast.
            </p>
          ) : null}
          {!hasMatches && (
            <div className="border-border space-y-3 border-t pt-4">
              <h3 className="font-bold">Kako da nastaviš pretragu</h3>
              <p className="text-muted-foreground text-sm">
                {diagnostics?.reasonCode === "unsupported_core_concern"
                  ? "Neke teme nisu povezane sa oblastima rada. Izmeni uslove i izaberi oblast za koju tražiš podršku."
                  : diagnostics?.reasonCode === "no_declared_core_coverage"
                    ? "Trenutno nema dostupnih profila koji se poklapaju sa izabranim temama. Proveri teme ili pregledaj imenik terapeuta."
                    : "Trenutno nema profila koji ispunjavaju sve ove uslove. Možeš da ih izmeniš ili da pregledaš imenik terapeuta."}
              </p>
              {criteria && diagnostics?.relaxations.length ? (
                <>
                  <p className="text-sm">
                    Pri ovoj pretrazi, ove izmene su dale dodatne profile.
                    Uslovi se menjaju tek kada primeniš izmene.
                  </p>
                  <ul className="space-y-2">
                    {diagnostics.relaxations.map((item) => (
                      <li key={item.criterion}>
                        <Button
                          type="button"
                          variant="outline"
                          className="h-auto max-w-full justify-start text-left whitespace-normal"
                          onClick={() => chooseRelaxation(item.criterion)}
                        >
                          {relaxationLabels[item.criterion]} (
                          {item.alternativeCount}{" "}
                          {item.alternativeCount === 1 ? "profil" : "profila"})
                        </Button>
                      </li>
                    ))}
                  </ul>
                </>
              ) : null}
              <a
                href="/terapeuti"
                className="text-sm underline underline-offset-4"
              >
                Pregledaj imenik terapeuta
              </a>
            </div>
          )}
        </>
      )}
      {editing && (
        <form id={`${formId}-form`} onSubmit={submit} className="space-y-5">
          <fieldset disabled={busy} className="space-y-5 disabled:opacity-70">
            <div className="space-y-2">
              <label
                htmlFor={`${formId}-concerns`}
                className="block text-sm font-medium"
              >
                Oblasti za koje tražiš podršku
              </label>
              <Input
                id={`${formId}-concerns`}
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Pretraži oblasti"
              />
              <ul className="flex flex-wrap gap-2">
                {draft.concernKeys.map((key) => (
                  <li
                    key={key}
                    className="bg-secondary inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm"
                  >
                    {catalog.data?.find((item) => item.key === key)?.label ??
                      draft.concernLabels[key] ??
                      key}
                    <button
                      type="button"
                      aria-label={`Ukloni oblast: ${draft.concernLabels[key] ?? key}`}
                      className="focus-visible:ring-ring rounded p-1 focus-visible:ring-2"
                      onClick={() =>
                        form.setValue(
                          "concernKeys",
                          draft.concernKeys.filter((item) => item !== key),
                          { shouldDirty: true },
                        )
                      }
                    >
                      <X className="size-4" />
                    </button>
                  </li>
                ))}
              </ul>
              {catalog.isPending ? (
                <p role="status" className="text-muted-foreground text-sm">
                  Učitavamo oblasti...
                </p>
              ) : catalog.isError ? (
                <p role="alert" className="text-sm">
                  Oblasti nisu učitane.{" "}
                  <button
                    type="button"
                    className="underline"
                    onClick={() => void catalog.refetch()}
                  >
                    Pokušaj ponovo
                  </button>
                </p>
              ) : (
                <div
                  className="border-input max-h-40 overflow-auto rounded-md border p-2"
                  aria-label="Dostupne oblasti"
                >
                  {visibleConcerns.length === 0 && (
                    <p
                      role="status"
                      className="text-muted-foreground px-2 py-2 text-sm"
                    >
                      Nema dodatnih oblasti za ovu pretragu. Pokušaj sa drugim
                      nazivom.
                    </p>
                  )}
                  {visibleConcerns.map((item) => (
                    <button
                      type="button"
                      key={item.key}
                      className="hover:bg-secondary focus-visible:bg-secondary block w-full rounded px-2 py-2 text-left text-sm"
                      onClick={() => {
                        form.setValue(
                          "concernKeys",
                          [...draft.concernKeys, item.key],
                          { shouldDirty: true },
                        );
                        form.setValue("concernLabels", {
                          ...draft.concernLabels,
                          [item.key]: item.label,
                        });
                        setQuery("");
                      }}
                    >
                      <span className="block">{item.label}</span>
                      {!!item.examples?.length && (
                        <span className="text-muted-foreground mt-1 block text-xs">
                          {item.examples.join(", ")}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              )}
              <p className="text-muted-foreground text-xs">
                Prednost imaju terapeuti koji rade sa više izabranih tema.
              </p>
            </div>
            {unsupported.length > 0 && (
              <div className="space-y-2">
                <p className="text-sm">
                  Ove teme nisu povezane sa oblastima iz liste. Možeš da ih
                  dopuniš iznad; preporuke se zasnivaju na prepoznatim temama.
                </p>
                <ul className="space-y-2">
                  {unsupported.map((text, index) => (
                    <li
                      key={`${text}-${index}`}
                      className="flex items-start justify-between gap-3 text-sm"
                    >
                      <span className="break-words">{text}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          form.setValue(
                            "unsupportedConcerns",
                            unsupported.filter((_, i) => index !== i),
                          )
                        }
                      >
                        Ukloni temu
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="space-y-2 text-sm font-medium">
                <span>Vrsta terapije</span>
                <select
                  className={selectClass}
                  {...form.register("therapyType")}
                >
                  <option value="INDIVIDUAL">Individualna terapija</option>
                  <option value="COUPLES">Partnerska terapija</option>
                </select>
              </label>
              <label className="space-y-2 text-sm font-medium">
                <span>Pol terapeuta</span>
                <select
                  className={selectClass}
                  {...form.register("therapistGender")}
                >
                  <option value="unknown">Nije važno</option>
                  <option value="FEMALE">Terapeutkinja</option>
                  <option value="MALE">Terapeut</option>
                </select>
              </label>
              <label className="space-y-2 text-sm font-medium">
                <span>
                  Najviša cena{" "}
                  {draft.therapyType === "COUPLES" ? "zajedničke " : ""}seanse
                  (€)
                </span>
                <Input
                  type="number"
                  min={0}
                  step="0.01"
                  placeholder="Bez ograničenja"
                  value={draft.requirements.maxSessionPrice ?? ""}
                  onChange={(event) =>
                    setNumber("maxSessionPrice", event.target.value)
                  }
                />
              </label>
              <label className="space-y-2 text-sm font-medium">
                <span>Uzrast osobe koja dolazi na terapiju</span>
                <Input
                  type="number"
                  min={0}
                  max={120}
                  step={1}
                  placeholder="Nije navedeno"
                  value={draft.requirements.clientAge ?? ""}
                  onChange={(event) =>
                    setNumber("clientAge", event.target.value)
                  }
                />
              </label>
            </div>
            <MultiSelect
              label="Jezici razgovora"
              value={draft.requirements.languages ?? []}
              options={LANGUAGE_OPTIONS}
              onChange={(value) =>
                form.setValue("requirements.languages", value)
              }
            />
            <details
              className="border-border border-t pt-4"
              open={
                (draft.requirements.modalities?.length ?? 0) > 0 ||
                Boolean(draft.requirements.excludeTrainees) ||
                draft.requirements.minYearsOfExperience !== undefined ||
                draft.requirements.minSessionDurationMinutes !== undefined
                  ? true
                  : undefined
              }
            >
              <summary className="cursor-pointer text-sm font-medium">
                Pristup, edukacija i iskustvo
              </summary>
              <div className="mt-4 space-y-4">
                <MultiSelect
                  label="Terapijski pravci"
                  value={draft.requirements.modalities ?? []}
                  options={approaches.data ?? []}
                  onChange={(value) =>
                    form.setValue("requirements.modalities", value)
                  }
                />
                <div className="grid gap-4 sm:grid-cols-2">
                  <label className="space-y-2 text-sm font-medium">
                    <span>Najmanje godina iskustva</span>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      value={draft.requirements.minYearsOfExperience ?? ""}
                      onChange={(event) =>
                        setNumber("minYearsOfExperience", event.target.value)
                      }
                    />
                  </label>
                  <label className="space-y-2 text-sm font-medium">
                    <span>Najkraće trajanje seanse (min)</span>
                    <Input
                      type="number"
                      min={1}
                      max={1440}
                      value={draft.requirements.minSessionDurationMinutes ?? ""}
                      onChange={(event) =>
                        setNumber(
                          "minSessionDurationMinutes",
                          event.target.value,
                        )
                      }
                    />
                  </label>
                </div>
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    {...form.register("requirements.excludeTrainees")}
                    className="mt-1"
                  />
                  Samo terapeuti sa navedenom završenom edukacijom
                </label>
              </div>
            </details>
          </fieldset>
          {noCriteria && (
            <p className="text-muted-foreground text-sm">
              Izaberi bar jednu oblast ili uslov pretrage.
            </p>
          )}
          {submitError && (
            <p role="alert" className="text-destructive text-sm">
              {submitError}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <Button
              type="submit"
              disabled={
                busy ||
                (unsupported.length > 0 && !draft.concernKeys.length) ||
                noCriteria
              }
            >
              {busy && <Loader2 className="mr-2 size-4 animate-spin" />}
              {navigating
                ? "Otvaramo rezultate..."
                : refine.isPending
                  ? "Tražimo terapeute..."
                  : "Primeni i pronađi terapeute"}
            </Button>
            <Button
              type="button"
              variant="ghost"
              disabled={busy}
              onClick={() => {
                resetEditor();
                setEditing(false);
              }}
            >
              Odustani
            </Button>
          </div>
          <p className="text-muted-foreground text-xs">
            Dobićeš novi rezultat. Prethodni rezultat i odgovori ostaju
            sačuvani.
          </p>
        </form>
      )}
    </section>
  );
}
